import { effectScope } from 'vue'
import type { CookieOptions, CookieRef } from '#app'

type SharedCookieApp = { _sharedCookies?: Map<string, CookieRef<unknown>> }

/**
 * `useCookie` re-parses `document.cookie`, opens a BroadcastChannel and adds a
 * watcher on every call. Composables that run once per product card made that
 * a hydration hotspot, so the client keeps one ref per cookie + options.
 *
 * The ref is created in a detached scope: Nuxt stops persisting writes once the
 * scope that created a cookie ref is disposed, which would break every other
 * holder after the first component unmounts.
 */
export function useSharedCookie<T>(name: string, opts?: CookieOptions<T>): CookieRef<T> {
  if (import.meta.server) return useCookie<T>(name, opts)
  const nuxtApp = useNuxtApp() as ReturnType<typeof useNuxtApp> & SharedCookieApp
  const cache = (nuxtApp._sharedCookies ||= new Map())
  const key = `${name}|${JSON.stringify(opts ?? {})}`
  let cookie = cache.get(key) as CookieRef<T> | undefined
  if (!cookie) {
    cookie = effectScope(true).run(() => useCookie<T>(name, opts))!
    cache.set(key, cookie as CookieRef<unknown>)
  }
  return cookie
}
